import React from "react";
import { styled } from "@mui/material/styles";
import Header from "../components/common/header/Header";
import Offers from "../components/common/BestSellProducts/BestSellProducts";
import { Box, Grid, Tab, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import theme from "../theme";
import CompactCarModelSearch from "../components/common/CarModelSearch/CompactCarModelSearch";
import TireVehiclePanel from "../components/common/TireSearch/TireVehiclePanel";
import SelectEngineRequirements from "../components/common/OilSearch/SelectEngineRequirements";
import SelectVehicle from "../components/common/OilSearch/SelectVehicle";
import { TabContext, TabList } from "@mui/lab";
import TabPanel from "@mui/lab/TabPanel";
import BatterySearch from "../components/common/BatterySearch/BatterySearch";
import BrandProductGroupSearch from "../components/common/BrandProductGroupSearch/BrandProductGroupSearch";
import Footer from "../components/common/Footer/Footer";

interface ItemProps {
  backgroundimage: string;
  width: string;
}

const Item = styled(Paper)<ItemProps>(({ theme, backgroundimage, width }) => ({
  backgroundColor: "#f1f1f1",
  borderRadius: 0,
  boxShadow: "none",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  position: "relative",
  minHeight: "150px",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 10,
    right: 8,
    bottom: 0,
    width: width,
    background: `url(${backgroundimage}) no-repeat`,
    backgroundSize: "contain",
    opacity: 0.7,
  },
}));

const SEARCH_CARD_MIN_HEIGHT = 360;
const SEARCH_CARD_STACKED_MIN_HEIGHT = 280;

const searchCardHeaderSx = {
  alignItems: "center",
  background: "#f1f1f1",
  color: theme.palette.text.secondary,
  padding: 1,
};

const searchCardItemSx = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: SEARCH_CARD_MIN_HEIGHT,
  "& > *": {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
};

const searchCardStackedItemSx = {
  ...searchCardItemSx,
  minHeight: SEARCH_CARD_STACKED_MIN_HEIGHT,
  flex: "0 0 auto",
};

const Home = () => {
  const [value, setValue] = React.useState("1");

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return (
    <div>
      <Header />
      <TireVehiclePanel />
      <Box sx={{ flexGrow: 1, m: 2, marginTop: 3 }}>
        <Grid container spacing={2} alignItems="flex-start">
          {/* Left: Vehicle search + brands/product groups */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ ...searchCardHeaderSx, width: "180px" }}>
                <Box
                  sx={{
                    alignItems: "center",
                    background: theme.palette.primary.main,
                  }}
                >
                  <Typography variant="subtitle1" align="center" component="h2">
                    Vehicle search
                  </Typography>
                </Box>
              </Box>
              <Item
                backgroundimage="./car.svg"
                width="10%"
                sx={searchCardItemSx}
              >
                <CompactCarModelSearch />
              </Item>
            </Box>
            <BrandProductGroupSearch />
          </Grid>

          {/* Right: Oil, then Battery */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ ...searchCardHeaderSx, width: "130px" }}>
                <Box
                  sx={{
                    alignItems: "center",
                    background: theme.palette.primary.main,
                  }}
                >
                  <Typography variant="subtitle1" align="center" component="h2">
                    Oil
                  </Typography>
                </Box>
              </Box>
              <Item
                backgroundimage="./oil.png"
                width="40px"
                sx={searchCardStackedItemSx}
              >
                <Box
                  sx={{
                    width: "100%",
                    typography: "body1",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="body1"
                    align="left"
                    sx={{
                      fontSize: "13px",
                      color: theme.palette.text.primary,
                      m: 1,
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    Search oil by
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                    }}
                  >
                    <TabContext value={value}>
                      <Box
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                          flexShrink: 0,
                        }}
                      >
                        <TabList
                          onChange={handleChange}
                          aria-label="oil search tabs"
                        >
                          <Tab
                            label="viscosity"
                            value="1"
                            sx={{
                              color: theme.palette.text.primary,
                              "&.Mui-selected": {
                                color: theme.palette.secondary.main,
                              },
                            }}
                          />
                          <Tab
                            label="vehicle"
                            value="2"
                            sx={{
                              color: theme.palette.text.primary,
                              "&.Mui-selected": {
                                color: theme.palette.secondary.main,
                              },
                            }}
                          />
                        </TabList>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                        }}
                      >
                        <TabPanel
                          value="1"
                          sx={{
                            p: 0,
                            flex: 1,
                            minHeight: 0,
                            display: value === "1" ? "flex" : "none",
                            flexDirection: "column",
                            "& > *": {
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              minHeight: 0,
                            },
                          }}
                        >
                          <SelectEngineRequirements />
                        </TabPanel>
                        <TabPanel
                          value="2"
                          sx={{
                            p: 0,
                            flex: 1,
                            minHeight: 0,
                            display: value === "2" ? "flex" : "none",
                            flexDirection: "column",
                            "& > *": {
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              minHeight: 0,
                            },
                          }}
                        >
                          <SelectVehicle />
                        </TabPanel>
                      </Box>
                    </TabContext>
                  </Box>
                </Box>
              </Item>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ ...searchCardHeaderSx, width: "130px" }}>
                <Box
                  sx={{
                    alignItems: "center",
                    background: theme.palette.primary.main,
                  }}
                >
                  <Typography variant="subtitle1" align="center" component="h2">
                    Battery
                  </Typography>
                </Box>
              </Box>
              <Item
                backgroundimage="./battery.png"
                width="10%"
                sx={searchCardStackedItemSx}
              >
                <BatterySearch />
              </Item>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          m: 2,
          marginTop: 3,
          background:
            "linear-gradient(90deg, rgba(0,212,255,0) 0%, rgba(12,41,63,1) 54%, rgba(2,0,36,1) 100%)",
          color: theme.palette.common.white,
        }}
      >
        <a href="https://tecmotion.co" target="_blank" rel="noreferrer">
          <Grid
            container
            spacing={1}
            sx={{
              color: theme.palette.text.secondary,
              textDecoration: "none !important",
            }}
          >
            <Grid item xs={12} sm={12} md={4}>
              <img
                src="./tecdoc_search_flow.png"
                alt="TecDoc Search Flow Diagram"
                style={{ height: "130px", flex: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={8}>
              <Box sx={{ m: 1 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Sell more parts with our Tecmotion catalogue solutions
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ fontSize: "13px" }}
                >
                  The automotive aftermarket is exceptional in its use and
                  management of data. In this data-driven environment, the
                  TecDoc catalogue is the product management industry
                  standard.
                  <br />
                  Tecmotion Catalogue enables companies to work efficiently at
                  all times and every point of sale and find the right part from
                  A to Z.
                </Typography>
                <img
                  style={{
                    width: "100px",
                    marginTop: 4,
                  }}
                  src="./tecmotion_white_logo.png"
                  alt="White Logo - TECMOTION"
                />
              </Box>
            </Grid>
          </Grid>
        </a>
      </Box>
      <Offers />
      <Footer />
    </div>
  );
};

export default Home;
